class NavigationDrawer extends Drawer {
  constructor() {
    super();

    this.ACTIVE_PANEL_CLASS = "navigation-drawer__panel--active";
    this.ACTIVE_SUBNAV_CLASS = "navigation-drawer__subnav--active";

    this._activePanel = null;
    this._activeSubNavigation = null;
  }

  /**
   * Lifecycle method called when the element is added to the DOM.
   */
  connectedCallback() {
    super.connectedCallback();

    this.activePanel = this.panels[0];
    this.activeSubNavigation = this.subNavigationArray[0];
    this.addButtonListeners();
  }

  /**
   * Adds event listeners to navigation and back buttons.
   */
  addButtonListeners() {
    this.backButtons.forEach(button => button.addEventListener("click", event =>
      this.navigateBack(event)
    ));

    this.navigationButtons.forEach(button => button.addEventListener("click", event =>
      this.navigateToSubNavigation(event)
    ));

    this.backButtons.forEach(button => button.addEventListener("keydown", event =>
      event.code === "Enter" && this.navigateBack(event)
    ));

    this.navigationButtons.forEach(button => button.addEventListener("keydown", event =>
      event.code === "Enter" && this.navigateToSubNavigation(event)
    ));
  }

  /**
   * Navigates to a sub navigation panel.
   * @param {Event} event
   */
  navigateToSubNavigation(event) {
    event.preventDefault();

    const target = event.currentTarget.dataset.navigation;
    const oldPanel = this.activePanel;

    const activeSubNavigation = this.subNavigationArray.find(nav => nav.dataset.subNavigation === target);

    if (!activeSubNavigation) return;

    const panel = activeSubNavigation.closest("[data-panel]");

    if (!panel) return;

    this.activePanel = panel;
    this.activeSubNavigation = activeSubNavigation;
    this.activePanel.classList.add(this.ACTIVE_PANEL_CLASS);
    this.activeSubNavigation.classList.add(this.ACTIVE_SUBNAV_CLASS);
    this.removePanelActiveClasses(oldPanel);
  }

  /**
   * Navigates back to the parent panel.
   * @param {Event} event
   */
  navigateBack(event) {
    event.preventDefault();

    const oldPanel = this.activePanel;
    const oldSubNavigation = this.getActiveSubNavigation();

    this.activePanel = this.panels[oldPanel.dataset.panel - 1];
    this.activePanel.classList.add(this.ACTIVE_PANEL_CLASS);
    this.removePanelActiveClasses(oldPanel, oldSubNavigation);
  }

  /**
   * Gets the active sub navigation within the active panel.
   * @returns {Element}
   */
  getActiveSubNavigation() {
    const panelIndex = this.activePanel.dataset.panel;

    return panelIndex === "1"
      ? this.activePanel.querySelector(`.${this.ACTIVE_SUBNAV_CLASS}`)
      : this.activeSubNavigation;
  }

  /**
   * Removes active classes from panel and sub navigation after transition ends.
   * @param {Element} panel
   * @param {Element} subNavigation
   */
  removePanelActiveClasses(panel, subNavigation) {
    this.activePanel.addEventListener("transitionend", () => {
      const index = this.activePanel.dataset.panel;
      const buttonToFocus = index > 0 ? this.backButtons[index - 1] : this.closeButton;

      removeTrapFocus(panel);

      panel.classList.remove(this.ACTIVE_PANEL_CLASS);
      subNavigation?.classList.remove(this.ACTIVE_SUBNAV_CLASS);

      trapFocus(this.activePanel, buttonToFocus);
    }, { once: true });
  }

  /**
   * Returns all back buttons.
   * @returns {NodeList}
   */
  get backButtons() {
    return this.querySelectorAll("[data-back-button]");
  }

  /**
   * Returns all navigation buttons.
   * @returns {NodeList}
   */
  get navigationButtons() {
    return this.querySelectorAll("[data-navigation]");
  }

  /**
   * Returns all panels.
   * @returns {NodeList}
   */
  get panels() {
    return this.querySelectorAll("[data-panel]");
  }

  /**
   * Returns all sub-navigation elements.
   * @returns {NodeList}
   */
  get subNavigation() {
    return this.querySelectorAll("[data-sub-navigation]");
  }

  /**
   * Returns array of sub-navigation elements.
   * @returns {Array}
   */
  get subNavigationArray() {
    return Array.from(this.subNavigation);
  }

  /**
   * Returns the currently active panel.
   * @returns {Element}
   */
  get activePanel() {
    return this._activePanel;
  }

  /**
   * Sets the currently active panel.
   * @param {Element} panel
   */
  set activePanel(panel) {
    this._activePanel = panel;
  }

  /**
   * Returns the currently active sub-navigation.
   * @returns {Element}
   */
  get activeSubNavigation() {
    return this._activeSubNavigation;
  }

  /**
   * Sets the currently active sub-navigation.
   * @param {Element} subNav
   */
  set activeSubNavigation(subNav) {
    this._activeSubNavigation = subNav;
  }
}

if (!customElements.get('navigation-drawer')) {
  customElements.define("navigation-drawer", NavigationDrawer);
}
